import { useState, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import i18n from '@/i18n';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { ActivityIcon } from '@/screens/workflow/components/activity/ActivityIcon';
import { getLocalizedField } from '@/utils/i18n';
import { ScrollArea } from './ui/scroll-area';
import { WorkflowTemplate } from '@/types/workflow';

const templates: WorkflowTemplate[] = [
  {
    id: uuidv4(),
    name: {
      'en-US': 'Questionnaire',
      'hu-HU': 'Kérdőív',
    },
    description: {
      'en-US': 'This workflow is triggered when a customer sends a message to the chatbot. It sends a message to the customer to remind them to complete their purchase.',
      'hu-HU': 'Ez a munkafolyamat akkor indul el, amikor egy ügyfél üzenetet küld a chatbotnak. Emailt küld a vevőnek, hogy megemlítse őket a vásárlás befejezéséhez.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Welcome Message',
      'hu-HU': 'Üdvözlő üzenet',
    },
    description: {
      'en-US': 'Sends an automated welcome message when a customer first interacts with the chatbot.',
      'hu-HU': 'Automatikus üdvözlő üzenetet küld, amikor egy ügyfél először lép kapcsolatba a chatbottal.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(), 
    name: {
      'en-US': 'Product Inquiry',
      'hu-HU': 'Termék érdeklődés',
    },
    description: {
      'en-US': 'Handles customer inquiries about specific products and provides detailed information.',
      'hu-HU': 'Kezeli az ügyfelek konkrét termékekkel kapcsolatos érdeklődését és részletes információkat nyújt.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Support Ticket Creation',
      'hu-HU': 'Támogatási jegy létrehozása',
    },
    description: {
      'en-US': 'Creates a support ticket when customers report issues through the chatbot.',
      'hu-HU': 'Támogatási jegyet hoz létre, amikor az ügyfelek problémákat jelentenek a chatboton keresztül.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Appointment Scheduler',
      'hu-HU': 'Időpontfoglalás',
    },
    description: {
      'en-US': 'Helps customers schedule appointments or consultations through the chatbot interface.',
      'hu-HU': 'Segít az ügyfeleknek időpontot foglalni vagy konzultációt egyeztetni a chatbot felületén keresztül.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'FAQ Assistant',
      'hu-HU': 'GYIK Asszisztens',
    },
    description: {
      'en-US': 'Provides automated responses to frequently asked questions and common customer inquiries.',
      'hu-HU': 'Automatikus válaszokat ad a gyakran ismételt kérdésekre és általános ügyfél kérdésekre.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Order Status Checker',
      'hu-HU': 'Rendelés állapot ellenőrző',
    },
    description: {
      'en-US': 'Allows customers to check their order status through the chatbot by providing their order number.',
      'hu-HU': 'Lehetővé teszi az ügyfelek számára, hogy ellenőrizzék rendelésük állapotát a chatboton keresztül a rendelési szám megadásával.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Feedback Collector',
      'hu-HU': 'Visszajelzés gyűjtő',
    },
    description: {
      'en-US': 'Collects customer feedback and satisfaction ratings after chat interactions.',
      'hu-HU': 'Összegyűjti az ügyfelek visszajelzéseit és elégedettségi értékeléseit a chat interakciók után.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Newsletter Subscription',
      'hu-HU': 'Hírlevél feliratkozás',
    },
    description: {
      'en-US': 'Enables customers to subscribe to newsletters through the chatbot conversation.',
      'hu-HU': 'Lehetővé teszi az ügyfelek számára a hírlevelekre való feliratkozást a chatbot beszélgetésen keresztül.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Live Agent Transfer',
      'hu-HU': 'Élő ügynök átirányítás',
    },
    description: {
      'en-US': 'Seamlessly transfers complex customer inquiries to available live support agents.',
      'hu-HU': 'Zökkenőmentesen továbbítja a összetett ügyfél kérdéseket az elérhető élő ügyfélszolgálati munkatársakhoz.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Product Recommendations',
      'hu-HU': 'Termék ajánlások',
    },
    description: {
      'en-US': 'Provides personalized product recommendations based on customer preferences and chat history.',
      'hu-HU': 'Személyre szabott termékajánlásokat nyújt az ügyfelek preferenciái és chat előzményei alapján.',
    },
    group: 'chatbot',
    trigger: 'new-chatbot-message',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Cart Abandonment',
      'hu-HU': 'Kosár elhagyás',
    },
    description: {
      'en-US': 'This workflow is triggered when a customer abandons their cart. It sends an email to the customer to remind them to complete their purchase.',
      'hu-HU': 'Ez a munkafolyamat akkor indul el, amikor egy ügyfél elhagyja a kosarát. Emailt küld a vevőnek, hogy megemlítse őket a vásárlás befejezéséhez.',
    },
    group: 'shoprenter',
    trigger: 'shoprenter-abandoned-order',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Cart Abandonment',
      'hu-HU': 'Kosár elhagyás',
    },
    description: {
      'en-US': 'This workflow is triggered when a customer abandons their cart. It sends an email to the customer to remind them to complete their purchase.',
      'hu-HU': 'Ez a munkafolyamat akkor indul el, amikor egy ügyfél elhagyja a kosarát. Emailt küld a vevőnek, hogy megemlítse őket a vásárlás befejezéséhez.',
    },
    group: 'shopify',
    trigger: 'shopify-abandoned-order',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Order Confirmation',
      'hu-HU': 'Rendelés megerősítése',
    },
    description: {
      'en-US': 'This workflow is triggered when a customer places an order. It sends an email to the customer to confirm their order.',
      'hu-HU': 'Ez a munkafolyamat akkor indul el, amikor egy ügyfél megrendelést ad ki. Emailt küld a vevőnek a megrendelés megerősítéséhez.',
    },
    group: 'shoprenter',
    trigger: 'shoprenter-order-status-change',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Order Confirmation',
      'hu-HU': 'Rendelés megerősítése',
    },
    description: {
      'en-US': 'This workflow is triggered when a customer places an order. It sends an email to the customer to confirm their order.',
      'hu-HU': 'Ez a munkafolyamat akkor indul el, amikor egy ügyfél megrendelést ad ki. Emailt küld a vevőnek a megrendelés megerősítéséhez.',
    },
    group: 'shopify',
    trigger: 'shopify-order-status-change',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Order Shipped',
      'hu-HU': 'Rendelés elküldve',
    },
    description: {
      'en-US': 'This workflow is triggered when a customer\'s order is shipped. It sends an email to the customer to confirm their order.',
      'hu-HU': 'Ez a munkafolyamat akkor indul el, amikor egy ügyfél rendelését elküldik. Emailt küld a vevőnek a rendelés elküldésének megerősítéséhez.',
    },
    group: 'shoprenter',
    trigger: 'shoprenter-order-status-change',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Order Shipped',
      'hu-HU': 'Rendelés elküldve',
    },
    description: {
      'en-US': 'This workflow is triggered when a customer\'s order is shipped. It sends an email to the customer to confirm their order.',
      'hu-HU': 'Ez a munkafolyamat akkor indul el, amikor egy ügyfél rendelését elküldik. Emailt küld a vevőnek a rendelés elküldésének megerősítéséhez.',
    },
    group: 'shopify',
    trigger: 'shopify-order-status-change',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Abandoned Cart Recovery',
      'hu-HU': 'Elhagyott kosár visszaszerzése',
    },
    description: {
      'en-US': 'Automatically send reminder emails to customers who abandoned their shopping carts.',
      'hu-HU': 'Automatikusan emlékeztető e-maileket küld azoknak az ügyfeleknek, akik elhagyták a bevásárlókosaraikat.',
    },
    group: 'shoprenter',
    trigger: 'shoprenter-abandoned-order',
  },
  {
    id: uuidv4(), 
    name: {
      'en-US': 'Abandoned Cart Recovery',
      'hu-HU': 'Elhagyott kosár visszaszerzése',
    },
    description: {
      'en-US': 'Automatically send reminder emails to customers who abandoned their shopping carts.',
      'hu-HU': 'Automatikusan emlékeztető e-maileket küld azoknak az ügyfeleknek, akik elhagyták a bevásárlókosaraikat.',
    },
    group: 'shopify',
    trigger: 'shopify-abandoned-order',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Product Back in Stock',
      'hu-HU': 'Termék újra készleten',
    },
    description: {
      'en-US': 'Notify customers when products they were interested in are back in stock.',
      'hu-HU': 'Értesítse az ügyfeleket, amikor az őket érdeklő termékek újra készleten vannak.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Product Back in Stock',
      'hu-HU': 'Termék újra készleten',
    },
    description: {
      'en-US': 'Notify customers when products they were interested in are back in stock.',
      'hu-HU': 'Értesítse az ügyfeleket, amikor az őket érdeklő termékek újra készleten vannak.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Customer Birthday Reward',
      'hu-HU': 'Születésnapi jutalom',
    },
    description: {
      'en-US': 'Send special birthday offers and discounts to customers on their birthday.',
      'hu-HU': 'Küldjön különleges születésnapi ajánlatokat és kedvezményeket az ügyfeleknek születésnapjukon.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Customer Birthday Reward',
      'hu-HU': 'Születésnapi jutalom',
    },
    description: {
      'en-US': 'Send special birthday offers and discounts to customers on their birthday.',
      'hu-HU': 'Küldjön különleges születésnapi ajánlatokat és kedvezményeket az ügyfeleknek születésnapjukon.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'First Purchase Welcome',
      'hu-HU': 'Első vásárlás üdvözlése',
    },
    description: {
      'en-US': 'Welcome new customers with a special thank you message after their first purchase.',
      'hu-HU': 'Üdvözölje az új ügyfeleket egy különleges köszönőüzenettel első vásárlásuk után.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'First Purchase Welcome',
      'hu-HU': 'Első vásárlás üdvözlése',
    },
    description: {
      'en-US': 'Welcome new customers with a special thank you message after their first purchase.',
      'hu-HU': 'Üdvözölje az új ügyfeleket egy különleges köszönőüzenettel első vásárlásuk után.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Review Request',
      'hu-HU': 'Értékelés kérése',
    },
    description: {
      'en-US': 'Automatically request product reviews from customers after their purchase.',
      'hu-HU': 'Automatikusan kérjen termékértékeléseket az ügyfelektől vásárlásuk után.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Review Request',
      'hu-HU': 'Értékelés kérése',
    },
    description: {
      'en-US': 'Automatically request product reviews from customers after their purchase.',
      'hu-HU': 'Automatikusan kérjen termékértékeléseket az ügyfelektől vásárlásuk után.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Price Drop Alert',
      'hu-HU': 'Árcsökkenési értesítés',
    },
    description: {
      'en-US': 'Notify customers when products they viewed have a price reduction.',
      'hu-HU': 'Értesítse az ügyfeleket, amikor az általuk megtekintett termékek ára csökken.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Price Drop Alert',
      'hu-HU': 'Árcsökkenési értesítés',
    },
    description: {
      'en-US': 'Notify customers when products they viewed have a price reduction.',
      'hu-HU': 'Értesítse az ügyfeleket, amikor az általuk megtekintett termékek ára csökken.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'VIP Customer Rewards',
      'hu-HU': 'VIP ügyfél jutalmak',
    },
    description: {
      'en-US': 'Send exclusive offers and rewards to customers who reach VIP status.',
      'hu-HU': 'Küldjön exkluzív ajánlatokat és jutalmakat a VIP státuszt elérő ügyfeleknek.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'VIP Customer Rewards',
      'hu-HU': 'VIP ügyfél jutalmak',
    },
    description: {
      'en-US': 'Send exclusive offers and rewards to customers who reach VIP status.',
      'hu-HU': 'Küldjön exkluzív ajánlatokat és jutalmakat a VIP státuszt elérő ügyfeleknek.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Seasonal Campaign',
      'hu-HU': 'Szezonális kampány',
    },
    description: {
      'en-US': 'Automated seasonal promotional campaigns for major shopping events.',
      'hu-HU': 'Automatizált szezonális promóciós kampányok a főbb vásárlási eseményekhez.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Seasonal Campaign',
      'hu-HU': 'Szezonális kampány',
    },
    description: {
      'en-US': 'Automated seasonal promotional campaigns for major shopping events.',
      'hu-HU': 'Automatizált szezonális promóciós kampányok a főbb vásárlási eseményekhez.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Loyalty Points Update',
      'hu-HU': 'Hűségpont frissítés',
    },
    description: {
      'en-US': 'Keep customers informed about their loyalty points balance and redemption opportunities.',
      'hu-HU': 'Tartsa az ügyfeleket naprakészen hűségpontjaik egyenlegéről és beváltási lehetőségeiről.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Loyalty Points Update',
      'hu-HU': 'Hűségpont frissítés',
    },
    description: {
      'en-US': 'Keep customers informed about their loyalty points balance and redemption opportunities.',
      'hu-HU': 'Tartsa az ügyfeleket naprakészen hűségpontjaik egyenlegéről és beváltási lehetőségeiről.',
    },
    group: 'shopify',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Win-Back Campaign',
      'hu-HU': 'Visszanyerési kampány',
    },
    description: {
      'en-US': 'Re-engage inactive customers with personalized offers and incentives.',
      'hu-HU': 'Inaktív ügyfelek újbóli bevonása személyre szabott ajánlatokkal és ösztönzőkkel.',
    },
    group: 'shoprenter',
    trigger: '',
  },
  {
    id: uuidv4(),
    name: {
      'en-US': 'Win-Back Campaign',
      'hu-HU': 'Visszanyerési kampány',
    },
    description: {
      'en-US': 'Re-engage inactive customers with personalized offers and incentives.',
      'hu-HU': 'Inaktív ügyfelek újbóli bevonása személyre szabott ajánlatokkal és ösztönzőkkel.',
    },
    group: 'shopify',
    trigger: '',
  }
];

const templatesByGroup = templates.reduce((acc, template) => {
  acc[template.group] = acc[template.group] || [];
  acc[template.group].push(template);
  return acc;
}, {} as Record<string, typeof templates>);

const templateGroups = Object.keys(templatesByGroup);

export function MwWorkflowTemplateSelector({
  selectedTemplate,
  onSelect,
}: {
  selectedTemplate: WorkflowTemplate | null;
  onSelect: (template: WorkflowTemplate) => void;
}) {
  const { t } = useTranslation('common');
  const { t: workflowT } = useTranslation('workflow');

  const [selectedTemplateGroup, setSelectedTemplateGroup] = useState<string>(templateGroups[0]);
  const [localTemplate, setLocalTemplate] = useState<WorkflowTemplate | null>(selectedTemplate);

  const handleSelect = (template: WorkflowTemplate) => {
    setLocalTemplate(template);
    onSelect(template);
  };

  return (
    <div className="grid md:grid-cols-10">
      <div className="col-span-3 bg-sidebar rounded-md p-2 flex flex-col gap-1">
        {
          templateGroups.map((group) => (
            <div key={group} className={cn(
              "cursor-pointer rounded-md hover:bg-sidebar-accent p-1 px-2 flex items-center gap-2",
              selectedTemplateGroup === group && 'bg-vbm/10 border border-vbm'
            )} onClick={() => setSelectedTemplateGroup(group)}>
              <ActivityIcon group={group} />
              <span className="text-sm font-medium">{workflowT(`groups.${group}`)}</span>
            </div>
          ))
        }
      </div>
      <ScrollArea className="md:max-h-[400px] col-span-7 px-4">
        <div className="grid grid-cols-2 gap-4">
          {
            templatesByGroup[selectedTemplateGroup].map((template) => (
              <div key={template.id} className={cn(
                "rounded-md border border-border p-2 cursor-pointer hover:border-vbm",
                localTemplate?.id === template.id && 'bg-vbm/10 border border-vbm'
              )} onClick={() => handleSelect(template)}>
                <p className="text-md font-medium">{getLocalizedField(template, 'name')}</p>
                <p className="text-xs text-muted-foreground">{getLocalizedField(template, 'description')}</p>
              </div>
            ))
          }
        </div>
      </ScrollArea>
    </div>
  );
}
